/*
 * <variant-slider> — progressive-enhancement wrapper that turns a list of
 * radio swatches for ONE non-color variant option (e.g. a gift-card
 * "Denomination" with many ordered values) into a single range slider.
 *
 * Why this design:
 * - The hidden radios stay the source of truth. <variant-selects> (cart.js)
 *   reads the *checked radio inside each fieldset* to resolve the variant, so
 *   the slider only has to move which radio is checked and re-dispatch a
 *   bubbling `change`. Nothing else in the variant pipeline has to change.
 * - No-JS / JS-error fallback: the radios + .swatch-lbl labels render normally.
 *   We only add the `slider-ready` class (which hides the swatches and reveals
 *   the track via CSS) once this element has successfully wired itself up, so a
 *   failure degrades visibly to working swatches rather than an inert control.
 * - Variant change (price/URL/media fetch) fires on `change` only — i.e. when
 *   the user releases the handle or presses an arrow key — not on every `input`
 *   tick while dragging, which would spam the section-render fetch.
 */
if (!customElements.get('variant-slider')) {
  customElements.define(
    'variant-slider',
    class VariantSlider extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('input[type="range"]');
        this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
        // The live value lives in the fieldset legend ([data-opt="<option>"]);
        // <variant-selects> also updates it on `change`, we keep it in sync live.
        this.display = this.closest('.product-form__input')?.querySelector('[data-opt]');

        if (!this.range || this.radios.length === 0) {
          // Structural problem (template changed out from under us) — surface it
          // and leave the swatch fallback visible rather than hiding it.
          console.warn('[variant-slider] missing range input or radios; leaving swatch fallback.', this);
          return;
        }

        this.range.max = String(this.radios.length - 1);
        const checkedIndex = this.radios.findIndex((radio) => radio.checked);
        this.range.value = String(checkedIndex < 0 ? 0 : checkedIndex);

        this.range.addEventListener('input', () => this.select(false));
        this.range.addEventListener('change', () => this.select(true));

        this.classList.add('slider-ready');
        this.select(false);
      }

      // index -> check that radio, update the live label + filled-track width,
      // and (only when committing) re-dispatch change so the rest of the
      // variant pipeline runs.
      select(commit) {
        const index = Number(this.range.value);
        const radio = this.radios[index];
        if (!radio) return;

        this.radios.forEach((r) => {
          r.checked = false;
        });
        radio.checked = true;

        if (this.display) this.display.textContent = radio.value;

        const max = Number(this.range.max) || 1;
        this.style.setProperty('--slider-progress', `${(index / max) * 100}%`);

        if (commit) radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
}
